import { useWindowDimensions } from "@luce/core";

import { buttonHitSlop } from "../../atoms/button/lib";
import { IconButton } from "../../atoms/icon-button";
import { Icon } from "../../atoms/icons";
import { Typography } from "../../atoms/typography";
import { cn, platformProps } from "../../lib";
import { minTouchTargetClassName } from "../../lib/touch-target";
import { Box, Pressable } from "../../primitives";
import { iconSize } from "../../tokens";
import { navHeaderIconColor, navHeaderSubtitleColor, navHeaderTitleColor } from "./lib";
import { navHeaderContainerVariants } from "./nav-header.variants";
import type { NavHeaderProps, NavHeaderVariant } from "./types";

function BackButton({ variant, onClick }: { variant: NavHeaderVariant; onClick: () => void }) {
  const { isDesktop } = useWindowDimensions();
  // On the brand and transparent bars it's IconButton's on-brand look, Figma's
  // 40 × 40 white icon button (NavHeader #6, DQ8)
  if (variant !== "white") {
    return (
      <IconButton
        icon="ArrowLeft"
        variant="on-brand"
        // Figma's 40 box and 24 arrow at every width, as the bar doesn't step
        // up. From 768 Large grows to 48: web takes Medium there (40 and 24 on
        // desktop), and iOS and Android tablets, which keep Large's phone
        // arrow, pin its box
        size={isDesktop ? "md" : "lg"}
        accessibilityLabel="Go back"
        onClick={onClick}
        className="md:h-cta-lg md:w-cta-lg"
      />
    );
  }
  // The white bar's: Figma's Neutral icon button with no fill or border, a dark
  // 24 arrow in a 40 box, which no IconButton look is. It takes the button
  // family's behaviour: neutral's hover and pressed fills, the shared focus
  // ring and a 44 tap area (NavHeader #6; coordinator, 2026-11-03)
  return (
    <Pressable
      onClick={onClick}
      accessibilityRole="button"
      accessibilityLabel="Go back"
      hitSlop={buttonHitSlop.lg}
      className={cn(
        "h-cta-lg w-cta-lg rounded-radius-full relative flex shrink-0 items-center justify-center transition-colors",
        // Pressed wins over hover: the gated hover rule comes later in the CSS,
        // so the pressed fill is repeated inside the same gate
        "[@media(hover:hover)]:hover:bg-luce-neutral-background-intermediate active:bg-luce-neutral-background-attenuated [@media(hover:hover)]:active:bg-luce-neutral-background-attenuated",
        "web:outline-none web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus",
        minTouchTargetClassName,
      )}
    >
      <Icon name="ArrowLeft" size={iconSize.lg} className={navHeaderIconColor[variant]} />
    </Pressable>
  );
}

export const NavHeader = ({
  variant = "brand",
  title,
  subtitle,
  onBack,
  right,
  className,
  testID,
}: NavHeaderProps) => {
  return (
    <Box
      {...platformProps({ testID, slot: "nav-header" })}
      className={cn(navHeaderContainerVariants({ variant, back: !!onBack }), className)}
    >
      {onBack && <BackButton variant={variant} onClick={onBack} />}

      {/* `min-w-0` lets the column shrink below its text on web, so a long
          title or subtitle truncates instead of pushing the right slot off.
          At least the 40 row, with Figma's `padding-xxs` 2 between the lines */}
      <Box className="gap-spacing-xxs min-h-spacing-3xl flex min-w-0 flex-1 flex-col justify-center">
        {title && (
          <Typography
            variant="label-md"
            color={navHeaderTitleColor[variant]}
            numberOfLines={1}
            // The screen's title: its page heading on web (native has no levels)
            accessibilityRole="header"
            as="h1"
            className="truncate"
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="body-sm"
            color={navHeaderSubtitleColor[variant]}
            numberOfLines={1}
            className="truncate"
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      {right && <Box className="gap-spacing-sm flex flex-row items-center">{right}</Box>}

      {/* Figma's white bar draws a 1px line on its bottom edge (the App Bar
          root's stroke). A layer inside the bar, so it stays 64 like the others */}
      {variant === "white" && (
        <Box className="h-outline-1 bg-luce-neutral-border-mediumEmphasis pointer-events-none absolute inset-x-0 bottom-0" />
      )}
    </Box>
  );
};

NavHeader.displayName = "NavHeader";
